import { useEffect, useState } from 'react';
import type { HabitView } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { ChevronLeft, Plus } from '@ui/shared/icons';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { CollapsibleGroup } from '@ui/shared/primitives/CollapsibleGroup';
import { habitCategoryMeta } from '@ui/shared/habits/habitCategory';

/**
 * Habit list screen (phase-2b-habits.md HB-16): flat list grouped
 * active-first, then a collapsed Paused group, then a collapsed Archived
 * group (desktop-manage.md SS1.1's grouping logic, layout-agnostic content
 * organization reused here per SS3's explicit "reuse, not desktop-specific
 * chrome" instruction), each row tappable into the editor, plus a
 * persistent "New habit" action reachable regardless of whether any habits
 * exist yet (AC-B17).
 */
export function HabitListScreen({
  onBack,
  onCreateHabit,
  onEditHabit,
}: {
  readonly onBack: () => void;
  readonly onCreateHabit: () => void;
  readonly onEditHabit: (habitId: string) => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const [habits, setHabits] = useState<readonly HabitView[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.listHabits({ includeArchived: true }).then((list) => {
      if (!cancelled) setHabits(list);
    });
    return () => {
      cancelled = true;
    };
  }, [api, version]);

  const active = habits?.filter((h) => h.state === 'active') ?? [];
  const paused = habits?.filter((h) => h.state === 'paused') ?? [];
  const archived = habits?.filter((h) => h.state === 'archived') ?? [];

  return (
    <main
      aria-label="Habits"
      style={{ padding: 'var(--space-6) var(--layout-page-margin-phone) var(--space-8)', maxWidth: '480px', margin: '0 auto' }}
    >
      <button
        type="button"
        onClick={onBack}
        aria-label="Back to Today"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
          color: 'var(--text-muted)',
          fontSize: 'var(--type-caption-size)',
          fontWeight: 600,
          minHeight: 'var(--touch-target-min)',
          marginLeft: '-8px',
        }}
      >
        <ChevronLeft size={16} aria-hidden="true" />
        Today
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 'var(--space-3)',
        }}
      >
        <h1
          style={{
            fontSize: 'var(--type-heading-size)',
            fontWeight: 'var(--type-heading-weight)',
            color: 'var(--text-primary)',
            margin: 0,
          }}
        >
          Habits
        </h1>
        <button
          type="button"
          onClick={onCreateHabit}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            color: 'var(--hue-primary)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
            minHeight: 'var(--touch-target-min)',
          }}
        >
          <Plus size={16} aria-hidden="true" />
          New habit
        </button>
      </div>

      {habits === null ? (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <SkeletonBlock height={56} />
          <div style={{ height: 'var(--space-3)' }} />
          <SkeletonBlock height={56} />
        </div>
      ) : habits.length === 0 ? (
        <p
          style={{
            fontSize: 'var(--type-body-size)',
            color: 'var(--text-secondary)',
            marginTop: 'var(--space-6)',
          }}
        >
          No habits yet. Set up your first one to see it on your daily page.
        </p>
      ) : (
        <div style={{ marginTop: 'var(--space-5)' }}>
          <HabitGroup label={null} habits={active} onSelect={onEditHabit} />
          {paused.length > 0 && (
            <CollapsibleGroup label="Paused">
              <HabitGroup label={null} habits={paused} onSelect={onEditHabit} />
            </CollapsibleGroup>
          )}
          {archived.length > 0 && (
            <CollapsibleGroup label="Archived">
              <HabitGroup label={null} habits={archived} onSelect={onEditHabit} />
            </CollapsibleGroup>
          )}
        </div>
      )}
    </main>
  );
}

function HabitGroup({
  label,
  habits,
  onSelect,
}: {
  readonly label: string | null;
  readonly habits: readonly HabitView[];
  readonly onSelect: (habitId: string) => void;
}) {
  if (habits.length === 0) return null;
  return (
    <div>
      {label && (
        <div
          style={{
            fontSize: 'var(--type-label-size)',
            lineHeight: 'var(--type-label-line)',
            letterSpacing: 'var(--type-label-tracking)',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            fontWeight: 600,
            marginBottom: 'var(--space-1)',
          }}
        >
          {label}
        </div>
      )}
      {habits.map((habit, i) => (
        <HabitRow key={habit.habitId} habit={habit} isLast={i === habits.length - 1} onSelect={() => onSelect(habit.habitId)} />
      ))}
    </div>
  );
}

function HabitRow({
  habit,
  isLast,
  onSelect,
}: {
  readonly habit: HabitView;
  readonly isLast: boolean;
  readonly onSelect: () => void;
}) {
  const meta = habitCategoryMeta(habit.category);
  const frequency = describeFrequency(habit);

  return (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) 0',
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: isLast ? '1px solid var(--border-subtle)' : 'none',
        textAlign: 'left',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '32px',
          height: '32px',
          borderRadius: 'var(--radius-sm)',
          background: `color-mix(in srgb, var(--accent-${meta.tokenSuffix}-vivid) 16%, var(--surface-raised))`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
        }}
      >
        <meta.Icon size={16} color={`var(--accent-${meta.tokenSuffix})`} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>
          {habit.name}
        </div>
        <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
          {habit.plan.habitKind === 'build' ? 'Build' : 'Avoid'} · {frequency}
        </div>
      </div>
      {habit.state !== 'active' && (
        <span
          style={{
            fontSize: 'var(--type-caption-size)',
            color: 'var(--text-muted)',
            flex: 'none',
          }}
        >
          {habit.state === 'paused' ? 'Paused' : 'Archived'}
        </span>
      )}
    </button>
  );
}

function describeFrequency(habit: HabitView): string {
  if (habit.plan.habitKind === 'build') {
    return habit.plan.schedule.kind === 'daily' ? 'Daily' : `${habit.plan.schedule.targetPerWeek}× a week`;
  }
  const limit = habit.plan.schedule.limitPerWeek;
  return limit === 0 ? 'Zero this week' : `At most ${limit} a week`;
}
